import { getEnvVar } from "@/lib/env";
import { fetchGitHubAPI, safeJsonParse } from "./githubApi";

interface GitHubLanguage {
  name: string;
  percentage: number;
  weight: number; // For compatibility with project skill format
}

/**
 * Fetch programming languages from a GitHub repository
 * @param repoUrl - GitHub repository URL
 * @returns Array of languages with percentages and weights
 */
export async function fetchGitHubLanguages(repoUrl: string): Promise<GitHubLanguage[]> {
  try {
    // Extract owner and repo from GitHub URL
    const match = repoUrl.match(/github\.com\/([^\/]+)\/([^\/]+)/);
    if (!match) {
      console.warn('Invalid GitHub URL format:', repoUrl);
      return [];
    }

    const [, owner, repo] = match;
    const cleanRepo = repo.replace(/\.git$/, '').replace(/\/$/, ''); // Remove .git suffix and trailing slash

    // Create headers with authentication if token is available
    const headers: HeadersInit = {
      'Accept': 'application/vnd.github.v3+json',
      'User-Agent': 'PersonalPortfolio'
    };

    // Add authentication header if token is available
    const githubToken = getEnvVar('VITE_GITHUB_TOKEN');
    if (githubToken) {
      headers['Authorization'] = `Bearer ${githubToken}`;
    }

    // Fetch languages data
    const languagesResponse = await fetchGitHubAPI(
      `https://api.github.com/repos/${owner}/${cleanRepo}/languages`,
      headers
    );

    if (!languagesResponse.ok) {
      console.warn('Failed to fetch GitHub languages:', languagesResponse.status);
      return [];
    }

    const languagesData = await safeJsonParse(languagesResponse);
    const totalBytes = Object.values(languagesData).reduce((sum: number, bytes: any) => sum + bytes, 0);

    if (totalBytes === 0) {
      return [];
    }

    // Convert to array with percentages and weights, sort by usage
    const languages = Object.entries(languagesData)
      .map(([name, bytes]: [string, any]) => {
        const percentage = Math.round((bytes / totalBytes) * 100);
        return {
          name,
          percentage,
          weight: percentage / 100, // Convert percentage to 0-1 weight for skill system
        };
      })
      .sort((a, b) => b.percentage - a.percentage)
      .slice(0, 10); // Keep top 10 languages

    return languages;
  } catch (error) {
    console.error('Error fetching GitHub languages:', error);
    return [];
  }
}

/**
 * Check if a GitHub URL is for an open source repository (not private)
 * @param gitUrl - GitHub repository URL
 * @returns true if the repository appears to be open source
 */
export function isOpenSourceGitHubRepo(gitUrl?: string): boolean {
  if (!gitUrl) return false;
  
  // Check if it's a GitHub URL and doesn't contain indicators of being private
  return (
    gitUrl.includes('github.com') &&
    !gitUrl.includes('not') &&
    !gitUrl.includes('inner source') &&
    !gitUrl.includes('private')
  );
}
